import { Controller } from "@hotwired/stimulus"

/**
 * Sortable Table Controller
 * 
 * A reusable controller for tables with sortable headers that intelligently
 * preserves URL parameters (filters, pagination, etc.).
 * 
 * Features:
 * - Preserves all existing URL params when sorting
 * - Visual feedback (hover effects, sort direction arrows)
 * - Resets pagination to page 1 on sort change
 * - Works standalone or integrated with filter forms
 * 
 * Usage:
 *   <table data-controller="sortable-table">
 *     <thead>
 *       <th data-sortable-table-target="header" data-column="name">Name</th>
 *       <th data-sortable-table-target="header" data-column="created_at">Created</th>
 *     </thead>
 *   </table>
 * 
 * Optional: Customize default sort direction
 *   <table data-controller="sortable-table" 
 *          data-sortable-table-default-direction-value="asc">
 * 
 * Optional: Disable page reset on sort
 *   <table data-controller="sortable-table" 
 *          data-sortable-table-reset-page-value="false">
 */
export default class extends Controller {
  static targets = ["header"]
  static values = {
    defaultDirection: { type: String, default: "desc" },
    resetPage: { type: Boolean, default: true }
  }

  connect() {
    this.initializeSortState()
    this.setupHeaders()
  }

  /**
   * Read current sort state from URL parameters
   */
  initializeSortState() {
    const params = new URLSearchParams(window.location.search)
    this.currentSort = params.get('sort')
    this.currentDirection = params.get('direction') || this.defaultDirectionValue
  }

  /**
   * Setup click handlers and visual indicators for sortable headers
   */
  setupHeaders() {
    this.headerTargets.forEach(header => {
      const column = header.dataset.column
      
      if (!column) {
        console.warn('Sortable header missing data-column attribute:', header)
        return
      }

      // Make header interactive
      this.makeHeaderSortable(header, column)
      
      // Mark currently sorted column
      if (column === this.currentSort) {
        this.markAsSorted(header, this.currentDirection)
      }
    })
  }

  /**
   * Make a header sortable with click handler
   */
  makeHeaderSortable(header, column) {
    header.style.cursor = 'pointer'
    header.setAttribute('role', 'button')
    header.setAttribute('aria-sort', column === this.currentSort ? this.currentDirection : 'none')
    
    header.addEventListener('click', (e) => {
      e.preventDefault()
      this.sort(column)
    })
  }

  /**
   * Mark header as currently sorted
   */
  markAsSorted(header, direction) {
    header.classList.add('current-sort', direction)
    header.setAttribute('aria-sort', direction === 'asc' ? 'ascending' : 'descending')
  }

  /**
   * Handle sort action
   */
  sort(column) {
    const nextDirection = this.calculateNextDirection(column)
    const url = this.buildSortUrl(column, nextDirection)
    
    // Trigger custom event for integration with other controllers
    this.dispatch('sort', { 
      detail: { column, direction: nextDirection, url: url.toString() }
    })
    
    // Navigate to sorted URL
    window.location.href = url.toString()
  }

  /**
   * Calculate next sort direction based on current state
   */
  calculateNextDirection(column) {
    if (column === this.currentSort) {
      // Toggle direction for same column
      return this.currentDirection === 'asc' ? 'desc' : 'asc'
    } else {
      // Use default direction for new column
      return this.defaultDirectionValue
    }
  }

  /**
   * Build URL with sort parameters while preserving existing params
   */
  buildSortUrl(column, direction) {
    const url = new URL(window.location.href)
    
    // Set sort parameters
    url.searchParams.set('sort', column)
    url.searchParams.set('direction', direction)
    
    // Reset pagination if enabled
    if (this.resetPageValue) {
      url.searchParams.delete('page')
    }
    
    return url
  }

  /**
   * Public API: Programmatically trigger sort
   * Useful for integration with filter forms
   */
  sortBy(column, direction = null) {
    const nextDirection = direction || this.calculateNextDirection(column)
    const url = this.buildSortUrl(column, nextDirection)
    window.location.href = url.toString()
  }

  /**
   * Public API: Clear sort parameters
   */
  clearSort() {
    const url = new URL(window.location.href)
    url.searchParams.delete('sort')
    url.searchParams.delete('direction')
    window.location.href = url.toString()
  }

  /**
   * Public API: Get current sort state
   */
  getCurrentSort() {
    return {
      column: this.currentSort,
      direction: this.currentDirection
    }
  }
}
